import { useState } from 'react';

interface SupplierAuthModalProps {
  onClose: () => void;
  onSuccess: (supplier: { name: string; city: string; contact: string }) => void;
}

export default function SupplierAuthModal({ onClose, onSuccess }: SupplierAuthModalProps) {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [step, setStep] = useState<1 | 2>(1);
  const [form, setForm] = useState({
    name: '',
    city: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    wechat: '',
    verifyCode: '',
  });
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const cities = ['上海', '北京', '香港', '新加坡', '悉尼', '迪拜', '伦敦', '纽约', '其他城市'];

  const sendCode = () => {
    setCodeSent(true);
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(timer); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (mode === 'signup') {
      if (!form.name.trim()) errs.name = '请输入您的姓名';
      if (!form.city) errs.city = '请选择所在城市';
      if (loginMethod === 'email' && !form.email.includes('@')) errs.email = '请输入有效邮箱';
      if (loginMethod === 'phone' && form.phone.length < 8) errs.phone = '请输入有效手机号';
      if (form.password.length < 6) errs.password = '密码至少6位';
      if (form.password !== form.confirmPassword) errs.confirmPassword = '两次密码不一致';
    } else {
      if (loginMethod === 'email' && !form.email.includes('@')) errs.email = '请输入有效邮箱';
      if (loginMethod === 'phone' && form.phone.length < 8) errs.phone = '请输入有效手机号';
      if (form.password.length < 1) errs.password = '请输入密码';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      onSuccess({
        name: form.name || '供应商用户',
        city: form.city || '上海',
        contact: loginMethod === 'email' ? form.email : form.phone,
      });
    }, 1200);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-rose-600 to-amber-500 px-8 py-6 relative">
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full">
              <i className="ri-store-2-line text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">供应商{mode === 'login' ? '登录' : '注册'}</h2>
              <p className="text-white/70 text-xs">本地仓储直邮平台</p>
            </div>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="flex border-b border-gray-100">
          {(['login', 'signup'] as const).map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setErrors({}); setStep(1); }}
              className={`flex-1 py-3 text-sm font-semibold transition-all cursor-pointer ${mode === m ? 'text-rose-600 border-b-2 border-rose-600' : 'text-gray-400 hover:text-gray-600'}`}
            >
              {m === 'login' ? '已有账号 · 登录' : '新供应商 · 注册'}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} data-readdy-form className="px-8 py-6 space-y-4">
          {/* Login Method Toggle */}
          <div className="flex gap-2 bg-gray-50 rounded-xl p-1">
            {(['email', 'phone'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setLoginMethod(m)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer ${loginMethod === m ? 'bg-white text-rose-600 shadow-sm' : 'text-gray-400'}`}
              >
                <i className={`${m === 'email' ? 'ri-mail-line' : 'ri-phone-line'} mr-1`}></i>
                {m === 'email' ? '邮箱' : '手机号'}
              </button>
            ))}
          </div>

          {/* Signup: Name + City */}
          {mode === 'signup' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">供应商姓名 / 店铺名称</label>
                <input
                  name="name"
                  type="text"
                  placeholder="请输入您的姓名或店铺名"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors"
                />
                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">仓储所在城市</label>
                <select
                  name="city"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors cursor-pointer bg-white"
                >
                  <option value="">请选择城市</option>
                  {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city}</p>}
              </div>
            </>
          )}

          {/* Email or Phone */}
          {loginMethod === 'email' ? (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">邮箱地址</label>
              <input
                name="email"
                type="email"
                placeholder="your@email.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors"
              />
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">手机号码</label>
              <div className="flex gap-2">
                <select className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-rose-400 bg-white cursor-pointer">
                  <option>+86</option>
                  <option>+852</option>
                  <option>+65</option>
                  <option>+61</option>
                  <option>+971</option>
                  <option>+44</option>
                  <option>+1</option>
                </select>
                <input
                  name="phone"
                  type="tel"
                  placeholder="请输入手机号"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors"
                />
              </div>
              {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
              {/* Verify Code */}
              <div className="flex gap-2 mt-2">
                <input
                  name="verifyCode"
                  type="text"
                  placeholder="验证码"
                  value={form.verifyCode}
                  onChange={(e) => setForm({ ...form, verifyCode: e.target.value })}
                  className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors"
                />
                <button
                  type="button"
                  onClick={sendCode}
                  disabled={countdown > 0}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${countdown > 0 ? 'bg-gray-100 text-gray-400' : 'bg-rose-50 text-rose-600 hover:bg-rose-100'}`}
                >
                  {countdown > 0 ? `${countdown}s` : '发送验证码'}
                </button>
              </div>
            </div>
          )}

          {/* Password */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">密码</label>
            <input
              name="password"
              type="password"
              placeholder={mode === 'signup' ? '设置密码（至少6位）' : '请输入密码'}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors"
            />
            {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
          </div>

          {mode === 'signup' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">确认密码</label>
                <input
                  name="confirmPassword"
                  type="password"
                  placeholder="再次输入密码"
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors"
                />
                {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">微信号（可选，方便买家联系）</label>
                <input
                  name="wechat"
                  type="text"
                  placeholder="您的微信号"
                  value={form.wechat}
                  onChange={(e) => setForm({ ...form, wechat: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors"
                />
              </div>
            </>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-gradient-to-r from-rose-600 to-amber-500 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i> 处理中...
              </span>
            ) : mode === 'login' ? '登录供应商账号' : '立即注册成为供应商'}
          </button>

          <p className="text-center text-xs text-gray-400">
            注册即表示同意
            <span className="text-rose-500 cursor-pointer"> 供应商服务协议 </span>
            及
            <span className="text-rose-500 cursor-pointer"> 隐私政策</span>
          </p>
        </form>
      </div>
    </div>
  );
}
